import { Home, Users, Eye, Star, TrendingUp, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdminStats } from '@/hooks/useAdminStats';
import { useProperties } from '@/hooks/useProperties';
import { formatCurrency, formatDate } from '@/lib/format';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';

export default function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useAdminStats();
  const { data: recentProperties, isLoading: propertiesLoading } = useProperties({ pageSize: 5 });

  const statCards = [
    { 
      title: 'Total de Imóveis', 
      value: stats?.totalProperties || 0, 
      icon: Home,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    { 
      title: 'Imóveis Ativos', 
      value: stats?.activeProperties || 0, 
      icon: TrendingUp,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    { 
      title: 'Total de Leads', 
      value: stats?.totalLeads || 0, 
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    { 
      title: 'Leads Novos', 
      value: stats?.newLeads || 0, 
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
    { 
      title: 'Visualizações', 
      value: stats?.totalViews || 0, 
      icon: Eye,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
    },
    { 
      title: 'Em Destaque', 
      value: stats?.featuredProperties || 0, 
      icon: Star,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-foreground sm:text-2xl">Dashboard</h1>
        <p className="text-sm text-muted-foreground sm:text-base">Visão geral do seu site</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="flex items-center gap-3 p-4 sm:gap-4 sm:p-6">
              <div className={`flex h-10 w-10 items-center justify-center rounded-full sm:h-12 sm:w-12 ${stat.bgColor}`}>
                <stat.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${stat.color}`} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-xs text-muted-foreground sm:text-sm">{stat.title}</p>
                {statsLoading ? (
                  <Skeleton className="mt-1 h-6 w-12 sm:h-8 sm:w-16" />
                ) : (
                  <p className="text-lg font-bold text-foreground sm:text-2xl">{stat.value}</p>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Properties */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Imóveis Recentes</CardTitle>
          <Link 
            to="/admin/imoveis" 
            className="text-sm text-primary hover:underline"
          >
            Ver todos
          </Link>
        </CardHeader>
        <CardContent>
          {propertiesLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {recentProperties?.properties.map((property) => (
                <div 
                  key={property.id}
                  className="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:gap-4"
                >
                  <div className="h-16 w-full flex-shrink-0 overflow-hidden rounded-md bg-muted sm:h-12 sm:w-16">
                    {property.main_image_url ? (
                      <img 
                        src={property.main_image_url} 
                        alt={property.title}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Home className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium text-foreground">{property.title}</p>
                    <p className="text-sm text-muted-foreground">{property.city}</p>
                  </div>
                  <div className="flex items-center justify-between sm:block sm:text-right">
                    <p className="font-semibold text-primary">{formatCurrency(property.price)}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(property.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
